import { useCallback } from 'react';
import type { PhieuNhapKho, PhieuNhapKhoInput } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UsePhieuNhapKhoMutationProps {
  detail: PhieuNhapKho | null;
  formMode: 'add' | 'edit' | 'view';
  selectedObj: PhieuNhapKho | null;
  shouldEditWithUpdatedData: boolean;
  editPhieuNhapKhoUuid: string | null;
  addItem: (data: PhieuNhapKhoInput) => Promise<PhieuNhapKho>;
  updateItem: (uuid: string, data: PhieuNhapKhoInput) => Promise<PhieuNhapKho>;
  deleteItem: (uuid: string) => Promise<unknown>;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  clearSelection: () => void;
  refreshData: () => Promise<void>;
  handleCloseEditWithUpdatedData: () => void;
}

export const usePhieuNhapKhoMutation = ({
  detail,
  formMode,
  selectedObj,
  shouldEditWithUpdatedData,
  editPhieuNhapKhoUuid,
  addItem,
  updateItem,
  deleteItem,
  handleCloseForm,
  handleCloseDelete,
  clearSelection,
  refreshData,
  handleCloseEditWithUpdatedData
}: UsePhieuNhapKhoMutationProps) => {
  const { entity } = useAuth();

  const handleSubmit = useCallback(
    async (data: PhieuNhapKhoInput) => {
      try {
        if (shouldEditWithUpdatedData && editPhieuNhapKhoUuid) {
          await updateItem(editPhieuNhapKhoUuid, data);
          handleCloseEditWithUpdatedData();
          return;
        }

        if (formMode === 'add') {
          await addItem(data);
        } else if (formMode === 'edit' && selectedObj?.uuid) {
          await updateItem(selectedObj.uuid, data);
        }

        handleCloseForm();
        clearSelection();
        await refreshData();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [
      addItem,
      clearSelection,
      editPhieuNhapKhoUuid,
      formMode,
      handleCloseEditWithUpdatedData,
      handleCloseForm,
      refreshData,
      selectedObj?.uuid,
      shouldEditWithUpdatedData,
      updateItem
    ]
  );

  const handleDelete = useCallback(async () => {
    const targetUuid = detail?.uuid ?? selectedObj?.uuid;

    if (!targetUuid) return;

    try {
      handleCloseForm();
      await deleteItem(targetUuid);
      handleCloseDelete();
      clearSelection();
      await refreshData();
    } catch (error) {
      console.error('Delete error:', error);
    }
  }, [clearSelection, deleteItem, detail?.uuid, handleCloseDelete, handleCloseForm, refreshData, selectedObj?.uuid]);

  const handleBulkDelete = useCallback(
    async (uuids: string[]) => {
      try {
        await api.post(`/entities/${entity?.slug}/erp/${QUERY_KEYS.PHIEU_NHAP_KHO}/bulk-delete/`, {
          list: uuids
        });
        await refreshData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        throw error;
      }
    },
    [entity?.slug, refreshData]
  );

  return {
    handleSubmit,
    handleDelete,
    handleBulkDelete
  };
};
